import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { format } from 'date-fns'

const { FiTrash2, FiFilter, FiCopy, FiChevronDown, FiChevronRight, FiCheck, FiX, FiClock } = FiIcons

const ApiLogPanel = ({ logs }) => {
  const [filter, setFilter] = useState('all')
  const [expandedLogs, setExpandedLogs] = useState(new Set())
  const [copySuccess, setCopySuccess] = useState('')
  const logEndRef = useRef(null)

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true
    if (filter === 'success') return log.type === 'success' || log.status < 400
    if (filter === 'error') return log.type === 'error' || log.status >= 400
    if (filter === 'ncb') return log.api === 'ncb'
    if (filter === 'dropbox') return log.api === 'dropbox'
    return true
  })

  const toggleLogExpansion = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev)
      if (newSet.has(logId)) {
        newSet.delete(logId)
      } else {
        newSet.add(logId)
      }
      return newSet
    })
  }

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopySuccess(id)
      setTimeout(() => setCopySuccess(''), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatJson = (obj) => {
    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj)
      } catch {
        return obj
      }
    }
    return JSON.stringify(obj, null, 2)
  }

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-50'
    if (status >= 400 && status < 500) return 'text-yellow-600 bg-yellow-50'
    if (status >= 500) return 'text-red-600 bg-red-50'
    return 'text-gray-600 bg-gray-50'
  }

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'text-green-700 bg-green-100'
      case 'POST': return 'text-blue-700 bg-blue-100'
      case 'PUT': return 'text-yellow-700 bg-yellow-100'
      case 'DELETE': return 'text-red-700 bg-red-100'
      default: return 'text-gray-700 bg-gray-100'
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">API Logs</h3>
          <div className="text-sm text-gray-500">
            {filteredLogs.length} of {logs.length} logs
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'All', count: logs.length },
            { id: 'success', label: 'Success', count: logs.filter(l => l.type === 'success' || (l.status && l.status < 400)).length },
            { id: 'error', label: 'Errors', count: logs.filter(l => l.type === 'error' || (l.status && l.status >= 400)).length },
            { id: 'ncb', label: 'NCB', count: logs.filter(l => l.api === 'ncb').length },
            { id: 'dropbox', label: 'Dropbox', count: logs.filter(l => l.api === 'dropbox').length }
          ].map((filterOption) => (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filter === filterOption.id
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filterOption.label} ({filterOption.count})
            </button>
          ))}
        </div>
      </div>

      {/* Logs */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <AnimatePresence>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <SafeIcon icon={FiClock} className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No logs yet. Execute an API request to see logs here.</p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
              >
                {/* Log Header */}
                <div
                  className="p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleLogExpansion(log.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <SafeIcon 
                        icon={expandedLogs.has(log.id) ? FiChevronDown : FiChevronRight} 
                        className="h-4 w-4 text-gray-400" 
                      />
                      
                      {log.method && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getMethodColor(log.method)}`}>
                          {log.method}
                        </span>
                      )}
                      
                      {log.status && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      )}
                      
                      <span className="text-sm font-medium text-gray-900">
                        {log.url || log.message}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      {log.duration && (
                        <span>{log.duration}ms</span>
                      )}
                      <span>{format(new Date(log.timestamp), 'HH:mm:ss')}</span>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedLogs.has(log.id) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-gray-200 bg-white"
                  >
                    <div className="p-4 space-y-4">
                      {/* Request Details */}
                      {log.request && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Request</h4>
                            <button
                              onClick={() => copyToClipboard(formatJson(log.request), `req-${log.id}`)}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                            >
                              <SafeIcon icon={copySuccess === `req-${log.id}` ? FiCheck : FiCopy} className="h-3 w-3 mr-1" />
                              {copySuccess === `req-${log.id}` ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto json-syntax">
                            {formatJson(log.request)}
                          </pre>
                        </div>
                      )}

                      {/* Response Details */}
                      {log.response && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Response</h4>
                            <button
                              onClick={() => copyToClipboard(formatJson(log.response), `res-${log.id}`)}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                            >
                              <SafeIcon icon={copySuccess === `res-${log.id}` ? FiCheck : FiCopy} className="h-3 w-3 mr-1" />
                              {copySuccess === `res-${log.id}` ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto json-syntax">
                            {formatJson(log.response)}
                          </pre>
                        </div>
                      )}

                      {/* Error Details */}
                      {log.error && (
                        <div>
                          <h4 className="text-sm font-medium text-red-700 mb-2">Error</h4>
                          <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-700">
                            {log.error}
                          </div>
                        </div>
                      )}

                      {/* Headers */}
                      {log.headers && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Headers</h4>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                            {formatJson(log.headers)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
        <div ref={logEndRef} />
      </div>
    </div>
  )
}

export default ApiLogPanel